import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import './App.css';


// Simple UUID generator if uuid package is not explicitly available
const uuidv4 = () => {
   return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
       (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
   );
};


const Results = () => {
   const [sourceResults, setSourceResults] = useState([]);
   const [responseText, setResponseText] = useState('');
   const [isLoading, setIsLoading] = useState(true);
   const [isDropdownOpen, setIsDropdownOpen] = useState(true);
   const location = useLocation();
   const [searchParams] = useSearchParams();
   const resultsParentDivRef = useRef(null);


   const [userId, setUserId] = useState("");
   const [timeZone, setTimeZone] = useState(null);


   // Prioritize URL parameters, then location state
   const query = searchParams.get('query') || location.state?.query;
   const websiteRef = searchParams.get('websiteRef') || location.state?.websiteRef;
   const selectedLanguage = searchParams.get('selected_language') || location.state?.selectedLanguage || 'English';


   const isFirstRender = useRef(true);


   useEffect(() => {
       const loadFingerprint = async () => {
           try {
               const fp = await FingerprintJS.load();
               const result = await fp.get();
               setUserId(result.visitorId);
               const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
               setTimeZone(userTimeZone);
           } catch (error) {
               console.error('Error loading fingerprint:', error);
           }
       };
       loadFingerprint();
   }, []);


   useEffect(() => {
       if (!query) {
           setIsLoading(false);
           return;
       }


       if (isFirstRender.current) {
           isFirstRender.current = false;
          
           const fetchData = async () => {
               try {
                   const params = new URLSearchParams({
                       query: query,
                       websiteRef: websiteRef || '',
                       user_id: userId,
                       time_zone: timeZone || '',
                       selected_language: selectedLanguage,
                       reaction: '0'
                   });


                   const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/chat/ai_search?${params.toString()}`);
                  
                   if (!response.ok) throw new Error('Request failed');
                  
                   const data = await response.json();
                   setSourceResults(data.source_results || []);
                   setResponseText(data.response || '');
               } catch (error) {
                   console.error('Error:', error);
                   setSourceResults([]);
                   setResponseText('');
               } finally {
                   setIsLoading(false);
               }
           };


           fetchData();
       }
   }, [query, websiteRef, selectedLanguage, userId, timeZone]);


   // Height adjustment logic
   useEffect(() => {
       const sendHeightToParent = () => {
           // Use setTimeout to ensure DOM has fully rendered
           setTimeout(() => {
               if (resultsParentDivRef.current) {
                   // If dropdown is closed, send height 0 to collapse the container
                   if (!isDropdownOpen) {
                       window.parent.postMessage({
                           type: 'resultsHeight',
                           height: 0
                       }, '*');
                       return;
                   }


                   // Get the height of the absolute positioned div inside the parent
                   const absoluteDiv = resultsParentDivRef.current.querySelector('.absolute');
                   if (absoluteDiv) {
                       const height = absoluteDiv.getBoundingClientRect().height;
                       window.parent.postMessage({
                           type: 'resultsHeight',
                           height: height
                       }, '*');
                   }
               }
           }, 100); // Small delay to ensure render
       };


       // Trigger height calculation on these dependencies
       sendHeightToParent();


       // Set up MutationObserver to catch dynamic content changes
       const observer = new MutationObserver(sendHeightToParent);


       if (resultsParentDivRef.current) {
           observer.observe(resultsParentDivRef.current, {
               childList: true,
               subtree: true,
               attributes: true
           });
       }


       // Add window resize listener for additional responsiveness
       window.addEventListener('resize', sendHeightToParent);


       return () => {
           observer.disconnect();
           window.removeEventListener('resize', sendHeightToParent);
       };
   }, [isLoading, sourceResults, responseText, isDropdownOpen]);


   const createMarkup = (html) => ({ __html: html });


   const handleCloseDropdown = () => {
       setIsDropdownOpen(false);
       // Force immediate height update
       setTimeout(() => {
           window.parent.postMessage({
               type: 'resultsHeight',
               height: 0
           }, '*');
       }, 50);
   };


   // Render logic if no query
   if (!query) {
       return (
           <div ref={resultsParentDivRef} className="relative w-full p-4">
               <div className="text-center text-gray-500">
                   No search query provided
               </div>
           </div>
       );
   }


   return (
       <div ref={resultsParentDivRef} className="relative w-full">
           {isDropdownOpen && (
               <div className="absolute w-full mt-1 border rounded-lg shadow-lg bg-white z-10 p-2">
                   {/* Close Button - Appears Only When Dropdown is Open */}
                   <button
                       onClick={handleCloseDropdown}
                       className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full transition"
                       style={{ backgroundColor: isLoading ? "#ececec" : "#f2f2f2" }} // Change color when loading
                   >
                       ✖
                   </button>


                   {isLoading ? (
                       <div className="loading flex justify-center items-center w-full" style={{ height: "100px", width: "100%", backgroundColor: "#ececec" }}>
                       </div>
                   ) : (
                       <>
                           {/* AI Response Section */}
                           {responseText && (
                               <div className="px-3 py-6 border-b border-gray-400">
                                   <div
                                       className="text-base text-gray-900 whitespace-pre-wrap"
                                       dangerouslySetInnerHTML={createMarkup(responseText.replace(/\n/g, '<br>'))}
                                   />
                               </div>
                           )}


                           {/* Source Results Section */}
                           {sourceResults.length > 0 && (
                               <>
                                   <div className="px-3 py-6 text-lg font-semibold border-b bg-gray-60">
                                       Sources
                                   </div>
                                   <ul className="max-h-80 overflow-y-auto border-b border-gray-400">
                                       {sourceResults.map((result, index) => {


                                           const cleanedResult = result.replace(/([^:]\/)\/+/g, "$1");


                                           const truncatedText = cleanedResult.length > 100 ? cleanedResult.slice(0, 100) + "..." : cleanedResult;


                                           return (
                                               <li key={index} className="px-3 py-4 hover:bg-gray-100 border-b last:border-b-0 border-gray-200 flex justify-between items-center">
                                                   <a
                                                       href={cleanedResult}
                                                       style={{ color: '#4900ff' }}
                                                       className="block text-blue-600 hover:underline flex-1 truncate"
                                                       target="_blank"
                                                       rel="noreferrer"
                                                   >
                                                       {truncatedText}
                                                   </a>
                                                   <ChevronRight style={{ color: '#4900ff' }} className="ml-2" size={20} />
                                               </li>
                                           );
                                       })}
                                   </ul>
                               </>
                           )}


                           {/* No Results Message */}
                           {!responseText && sourceResults.length === 0 && (
                               <div className="px-3 py-6 text-xl font-semibold text-gray-900 border-b bg-gray-60">
                                   {query ? 'No results found' : 'Type to search'}
                               </div>
                           )}


                           {/* Footer */}
                           <div className="px-2 py-3 text-center border-t">
                               <p className="text-[12px] text-[#434343]">
                                   Powered by <a href="https://www.revize.com/" className="underline" target="_blank" rel="noopener noreferrer">revize.AI,</a> the Government Website Experts {' '}
                                   <a href="https://www.revize.com/ai_terms/" className="underline" target="_blank" rel="noopener noreferrer">Terms of use</a> {' '}
                                   <a href="https://www.revize.com/ai_privacy/" className="underline" target="_blank" rel="noopener noreferrer">Privacy</a>
                               </p>
                           </div>
                       </>
                   )}
               </div>
           )}
       </div>
   );
};


export default Results;

